/**
 * Exporting a preserved copy (F-JR-04, F-JP-33).
 *
 * The safety rule is not in this file, and that is deliberate: the path check
 * (one of six approved roots, exact or prefix-plus-separator) and the SHA-256
 * re-read both belong to the repository boundary, because only the repository
 * knows where its own roots are and only it can read the bytes a second time.
 * `JournalRepositoryPort.exportRecovery` returns the verified bytes or
 * `unsafeRecoveryPath`, and this module's whole job is to make sure nothing
 * else can happen in between.
 *
 * What *is* enforced here is the second half of F-JP-33: **the raw recovery URL
 * is never shared.** The export name is built from the item's kind and short id
 * — `journalRecoveryExportName` — and the item's `path` reaches nothing. A
 * quarantined file's location is a fact about the app's private storage, and a
 * share sheet is the one place it would leave the device.
 *
 * The native build staged a copy on disk and deleted it when the share sheet
 * dismissed. The web `ExportPort` takes bytes instead of a path (§9.3), so
 * there is no staged copy to leak and nothing to sweep — the lifecycle the
 * native code needed is gone rather than reimplemented.
 */

import type {
  JournalRecoveryItem,
  JournalRepositoryErrorCode,
} from "@ff/domain";
import type { ExportOutcome, ExportPort, JournalRepositoryPort } from "@ff/domain/ports";
import {
  journalRecoveryExportName,
  sanitizeJournalExportExtension,
  sanitizeJournalExportFilename,
} from "@ff/domain";

/**
 * A preserved record may not decode at all — that is why it is preserved — so
 * the copy is offered as opaque bytes. Claiming a content type the file may not
 * honour would invite the receiving app to parse it, and a quarantined file is
 * the last thing that should be reparsed on the way out.
 */
export const RECOVERY_EXPORT_MIME_TYPE = "application/octet-stream";

export type RecoveryExportResult =
  | { readonly kind: "exported"; readonly filename: string; readonly outcome: ExportOutcome }
  /** Verification refused. The UI shows the F-JR-04 failure alert. */
  | { readonly kind: "rejected"; readonly error: JournalRepositoryErrorCode };

/**
 * The name the user is offered.
 *
 * The extension is taken from the preserved file's own name so a `.json` record
 * still arrives as one, and it goes through `sanitizeJournalExportExtension`
 * — lowercase ASCII letters, at most twelve, falling back to "data" — so a
 * path segment like `.json.exe` or a bare dot cannot become the offered
 * extension. The stem is `journalRecoveryExportName`, which is kind plus short
 * id and contains no part of the path.
 */
export function recoveryExportFilename(item: JournalRecoveryItem): string {
  const stem = sanitizeJournalExportFilename(journalRecoveryExportName(item));
  // Only a real extension counts: `lastIndexOf` on a name with no dot returns
  // -1, and a leading dot is a hidden-file marker rather than a type.
  const lastSegment = item.path.slice(item.path.lastIndexOf("/") + 1);
  const dot = lastSegment.lastIndexOf(".");
  const rawExtension = dot > 0 ? lastSegment.slice(dot + 1) : "";
  return `${stem}.${sanitizeJournalExportExtension(rawExtension)}`;
}

export interface RecoveryExportRequest {
  readonly repository: JournalRepositoryPort;
  readonly exporter: ExportPort;
  readonly item: JournalRecoveryItem;
}

/**
 * Re-verify, then offer. Never the other way round, and never one without the
 * other: a copy offered before verification is a copy of bytes nobody checked.
 */
export async function exportPreservedCopy(
  request: RecoveryExportRequest,
): Promise<RecoveryExportResult> {
  const verified = await request.repository.exportRecovery(request.item);
  if (verified.kind === "rejected") {
    return { kind: "rejected", error: verified.error };
  }

  // The name is derived from the *verified* item the repository returned, not
  // from the one the caller held: a row rendered from a stale enumeration must
  // not be able to name a file whose bytes were proven under a different id.
  const filename = recoveryExportFilename(verified.item);
  const outcome = await request.exporter.offer(
    filename,
    RECOVERY_EXPORT_MIME_TYPE,
    verified.bytes,
  );
  return { kind: "exported", filename, outcome };
}
